import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ChatModal from '../components/ChatModal';
import ShareButton from '../components/ShareButton';
import './ProductDetails.css';
import { apiUrl } from '../api';

function ProductDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const token = localStorage.getItem('token');
  const currentUserId = localStorage.getItem('userId');

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await fetch(apiUrl(`/api/products/${id}`));
        if (!response.ok) {
          throw new Error('Product not found');
        }
        const data = await response.json();
        setProduct(data);
        setSelectedImageIndex(0);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  const handleMakeOffer = () => {
    if (!token) {
      alert('Please login first to make an offer');
      navigate('/login');
      return;
    }
    setIsChatOpen(true);
  };

  const handleChat = () => {
    if (!token) {
      alert('Please login first to chat');
      navigate('/login');
      return;
    }
    setIsChatOpen(true);
  };

  const maskPhoneNumber = (phone) => {
    if (!phone) return null;
    const cleanPhone = String(phone).trim();
    if (cleanPhone.length > 4) {
      return `${cleanPhone.slice(0, 4)}xxxxxx`;
    }
    return cleanPhone;
  };

  if (loading) {
    return (
      <div className="product-details">
        <div className="loading-spinner-container">
          <div className="spinner"></div>
          <p>Loading product details...</p>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="product-details">
        <div className="error-card">
          <p>Error: {error || 'Product not found'}</p>
          <button className="back-btn" onClick={() => navigate('/')}>← Back to Products</button>
        </div>
      </div>
    );
  }

  const isOwnProduct = parseInt(currentUserId) === parseInt(product.user_id);
  const images = Array.isArray(product.image_urls) ? product.image_urls : [];
  const selectedImage = images[selectedImageIndex];

  const showPreviousImage = () => {
    setSelectedImageIndex((currentIndex) => Math.max(0, currentIndex - 1));
  };

  const showNextImage = () => {
    setSelectedImageIndex((currentIndex) => Math.min(images.length - 1, currentIndex + 1));
  };

  const formattedDate = product.created_at
    ? new Date(product.created_at).toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      })
    : null;

  return (
    <div className="product-details">
      <button className="back-btn" onClick={() => navigate('/')}>
        <span className="back-arrow">←</span> Back to Explore
      </button>

      <div className="details-container">
        {/* Images Section */}
        <div className="images-section">
          {images.length > 0 ? (
            <div className="image-gallery">
              <div className="main-image">
                <img src={selectedImage} alt={`${product.title} ${selectedImageIndex + 1}`} />
                {images.length > 1 && (
                  <>
                    <button
                      type="button"
                      className="gallery-arrow gallery-arrow-previous"
                      onClick={showPreviousImage}
                      disabled={selectedImageIndex === 0}
                      aria-label="View previous image"
                    >
                      ‹
                    </button>
                    <button
                      type="button"
                      className="gallery-arrow gallery-arrow-next"
                      onClick={showNextImage}
                      disabled={selectedImageIndex === images.length - 1}
                      aria-label="View next image"
                    >
                      ›
                    </button>
                    <span className="gallery-counter">
                      {selectedImageIndex + 1} / {images.length}
                    </span>
                  </>
                )}
              </div>
              <div className="image-thumbnails" role="list" aria-label="Product images">
                {images.map((image, index) => (
                  <button
                    type="button"
                    className={`image-thumbnail ${selectedImageIndex === index ? 'active' : ''}`}
                    onClick={() => setSelectedImageIndex(index)}
                    key={`${image}-${index}`}
                    aria-label={`View image ${index + 1}`}
                  >
                    <img src={image} alt="" />
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="main-image placeholder">No images uploaded</div>
          )}
        </div>

        {/* Product Info Section */}
        <div className="info-section">
          <div className="header-info">
            <h1 className="product-title">{product.title}</h1>
            <div className="price-badge-row">
              <p className="price">₹{Math.trunc(Number(product.price || 0)).toLocaleString('en-IN')}</p>
              {product.negotiable && <span className="negotiable-badge">Negotiable</span>}
            </div>
          </div>

          {/* Quick Details Grid */}
          <div className="details-grid">
            <div className="detail-item highlight-item">
              <label>Location</label>
              <span className="bold-value">{product.locality}, {product.city}</span>
            </div>

            {formattedDate && (
              <div className="detail-item highlight-item">
                <label>Date Posted 🗓️</label>
                <span className="bold-value">{formattedDate}</span>
              </div>
            )}

            <div className="detail-item">
              <label>Condition</label>
              <span className="item-badge">{product.condition}</span>
            </div>

            <div className="detail-item">
              <label>Category</label>
              <span className="regular-value">{product.category}</span>
            </div>

            {product.brand && (
              <div className="detail-item">
                <label>Brand</label>
                <span className="regular-value">{product.brand}</span>
              </div>
            )}
          </div>

          {/* Description Section */}
          <div className="section-card description-section">
            <h3 className="section-heading">Description</h3>
            <p className="description-content">{product.description || 'No description provided.'}</p>
          </div>

          {/* Seller Section */}
          <div className="section-card seller-section">
            <h3 className="section-heading">Seller Information</h3>
            <div className="seller-profile">
              <div className="seller-avatar">
                {product.username?.charAt(0).toUpperCase() || 'U'}
              </div>
              <div className="seller-info">
                <p className="seller-name">{product.username}</p>
                {product.phone && (
                  <p className="seller-contact">📞 {maskPhoneNumber(product.phone)}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Action Footer */}
      <div className="action-buttons-wrapper">
        <div className="action-buttons">
          <ShareButton product={product} />
          {!isOwnProduct && (
            <>
              <button className="offer-btn" onClick={handleMakeOffer}>Make an Offer</button>
              <button className="chat-btn" onClick={handleChat}>Chat with Seller</button>
            </>
          )}
        </div>
      </div>

      {!isOwnProduct && (
        <ChatModal
          isOpen={isChatOpen}
          onClose={() => setIsChatOpen(false)}
          productId={product.id}
          receiverId={product.user_id}
          receiverUsername={product.username}
          currentUserId={currentUserId}
        />
      )}
    </div>
  );
}

export default ProductDetails;